import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useConfig } from "../../../context/ConfigContext";
import { formatNumMil, formatNumKgT, formatQTZ } from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";

const ResultsStep = ({
  locationData,
  consumptionData,
  systemDesign,
  financingData,
  onRequestConsultation,
}) => {
  const config = useConfig();
  const {
    COMPANY_NAME,
    WHATSAPP_NUMBER_CHAT,
    GARANTY_YEARS,
    FACT_CO2_PROMEDIO_ANUAL,
    FACT_ARBOLES_PROM_ANUAL,
    FACT_CARS_OFF_PROM_ANUAL,
    FACT_CARBON_PROM_ANUAL,
  } = config;
  const [showEnvironmentalImpact, setShowEnvironmentalImpact] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: "",
    email: "",
    phone: "",
    preferredContact: "phone",
  });

  // Calculate environmental impact
  const calculateEnvironmentalImpact = () => {
    const annualGeneration = systemDesign?.monthlyGeneration * 12;
    const co2Reduction = annualGeneration * FACT_CO2_PROMEDIO_ANUAL; // toneladas de CO2 per kWh in Guatemala
    const treesEquivalent = co2Reduction * FACT_ARBOLES_PROM_ANUAL; // toneladas de CO2 absorbed per tree per year
    const carsOffRoad = co2Reduction / FACT_CARS_OFF_PROM_ANUAL; // toneladas de CO2 per car per year

    return {
      annualCO2Reduction: co2Reduction,
      treesEquivalent: roundTo(treesEquivalent, 3),
      carsOffRoad: roundTo(carsOffRoad, 3),
      coalAvoided: roundTo(co2Reduction * FACT_CARBON_PROM_ANUAL), // tons of coal
    };
  };

  const environmentalImpact = calculateEnvironmentalImpact();

  // Generate energy production chart data
  const generateMonthlyProduction = () => {
    const monthlyVariation = [
      0.85, 0.9, 0.95, 1.05, 1.1, 1.15, 1.1, 1.05, 1.0, 0.95, 0.9, 0.85,
    ];
    const months = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];

    return months?.map((month, index) => ({
      month,
      production: Math.round(
        systemDesign?.monthlyGeneration * monthlyVariation?.[index]
      ),
      consumption: consumptionData?.monthlyKwh,
      savings: Math.round(
        systemDesign?.monthlySavings * monthlyVariation?.[index]
      ),
    }));
  };

  const monthlyData = generateMonthlyProduction();

  const handleRequestConsultation = () => {
    const annualGeneration = systemDesign?.monthlyGeneration * 12;
    const leadSummary =
      `*SOLICITUD DE CONSULTA ${COMPANY_NAME.toUpperCase()}*\n\n` +
      `*DATOS DEL CLIENTE:*\n` +
      `👤 Nombre: ${contactInfo?.name}\n` +
      `📧 Email: ${contactInfo?.email}\n` +
      `📞 Teléfono: ${contactInfo?.phone}\n` +
      `💬 Preferencia: ${contactInfo?.preferredContact}\n\n` +
      `*CONFIGURACIÓN DEL SISTEMA:*\n` +
      `📍 Región: ${locationData?.zoneName}\n` +
      `⚡ Consumo: ${consumptionData?.monthlyKwh} kWh/mes\n` +
      `🏗️ Tamaño: ${systemDesign?.systemSize} kW\n` +
      `☀️ Paneles: ${systemDesign?.panels}\n\n` +
      `*ANÁLISIS FINANCIERO:*\n` +
      `💰 Inversión Neta: ${formatQTZ(financingData?.totalCost)}\n` +
      `💎 Ahorro Mensual: ${formatQTZ(systemDesign?.monthlySavings)}\n` +
      `📈 Ahorro 25 años: ${formatQTZ(financingData?.totalSavings25Years)}\n` +
      `⏱️ ROI: ${systemDesign?.paybackYears} años\n\n` +
      `*IMPACTO AMBIENTAL:*\n` +
      `🌳 Árboles: ${environmentalImpact?.treesEquivalent} por año\n` +
      `☁️ CO2 evitado: ${formatNumKgT(environmentalImpact?.annualCO2Reduction)}\n\n` +
      `_Generado por Calculadora Solar de ${COMPANY_NAME}_`;

    const calculationData = {
      location: locationData,
      consumption: consumptionData,
      system: systemDesign,
      financing: financingData,
      contact: contactInfo,
      leadSummary, // Passing the formatted text
      timestamp: new Date()?.toISOString(),
    };

    onRequestConsultation(calculationData);
  };

  const shareViaWhatsApp = () => {
    const message =
      `*¡PROPUESTA SOLAR - ${COMPANY_NAME.toUpperCase()}!* 🌞🏦\n\n` +
      `He calculado mi sistema solar y estos son los resultados:\n\n` +
      `📊 *Consumo Actual:* ${consumptionData?.monthlyKwh} kWh/mes\n` +
      `⚡ *Sistema Recomendado:* ${systemDesign?.systemSize} kW (${systemDesign?.panels} paneles)\n` +
      `💰 *Nueva Factura Estimada:* ${formatQTZ(systemDesign?.monthlyBillAfter)}\n` +
      `✨ *Ahorro Mensual:* ${formatQTZ(systemDesign?.monthlySavings)}\n` +
      `⏱️ *Recuperación:* ${systemDesign?.paybackYears} años\n` +
      `🚀 *Inversión Neta:* ${formatQTZ(financingData?.totalCost)}\n\n` +
      `¡Calcula el tuyo también aquí! 👇\n` +
      `${window.location?.origin}/calculadora`;

    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const contactViaWhatsApp = () => {
    const annualGeneration = systemDesign?.monthlyGeneration * 12;
    const leadSummary =
      `*NUEVA SOLICITUD DE CONSULTA SOLARYG*\n\n` +
      `*DATOS DEL CLIENTE:*\n` +
      `👤 Nombre: ${contactInfo?.name}\n` +
      `📧 Email: ${contactInfo?.email}\n` +
      `📞 Teléfono: ${contactInfo?.phone}\n` +
      `💬 Preferencia: WhatsApp\n\n` +
      `*CONFIGURACIÓN DEL SISTEMA:*\n` +
      `📍 Región: ${locationData?.zoneName}\n` +
      `⚡ Consumo: ${consumptionData?.monthlyKwh} kWh/mes\n` +
      `🏗️ Tamaño: ${systemDesign?.systemSize} kW\n` +
      `☀️ Paneles: ${systemDesign?.panels}\n\n` +
      `*ANÁLISIS FINANCIERO:*\n` +
      `💰 Inversión Neta: ${formatQTZ(financingData?.totalCost)}\n` +
      `💎 Ahorro Mensual: ${formatQTZ(systemDesign?.monthlySavings)}\n` +
      `📈 Ahorro 25 años: ${formatQTZ(financingData?.totalSavings25Years)}\n` +
      `⏱️ ROI: ${systemDesign?.paybackYears} años\n\n` +
      `_Generado por Calculadora Solar de ${COMPANY_NAME}_`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER_CHAT?.replace(/[^0-9]/g, "")}&text=${encodeURIComponent(leadSummary)}`;
    window.open(whatsappUrl, "_blank");
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <Icon name="CheckCircle" size={32} className="text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-foreground mb-2">
          ¡Tu Sistema Solar Está Listo!
        </h2>
        <p className="text-lg text-muted-foreground">
          Aquí están los resultados personalizados para tu propiedad
        </p>
      </div>

      {/* System Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl p-6 text-center">
          <Icon name="Sun" size={32} className="text-yellow-600 mx-auto mb-3" />
          <div className="text-2xl font-bold text-foreground mb-1">
            {systemDesign?.systemSize} kWh./mes
          </div>
          <div className="text-sm text-muted-foreground">
            {systemDesign?.panels} Paneles Solares
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-50 to-teal-50 border border-cyan-200 rounded-xl p-6 text-center">
          <Icon
            name="Receipt"
            size={32}
            className="text-cyan-600 mx-auto mb-3"
          />
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatQTZ(systemDesign?.monthlyBillAfter)}
          </div>
          <div className="text-sm text-muted-foreground">Nueva Factura</div>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 text-center">
          <Icon
            name="DollarSign"
            size={32}
            className="text-green-600 mx-auto mb-3"
          />
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatQTZ(systemDesign?.monthlySavings)}
          </div>
          <div className="text-sm text-muted-foreground">Ahorro Mensual</div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 text-center">
          <Icon
            name="TrendingUp"
            size={32}
            className="text-blue-600 mx-auto mb-3"
          />
          <div className="text-2xl font-bold text-foreground mb-1">
            {systemDesign?.paybackYears} años
          </div>
          <div className="text-sm text-muted-foreground">
            Tiempo de Recuperación
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6 text-center">
          <Icon
            name="Percent"
            size={32}
            className="text-purple-600 mx-auto mb-3"
          />
          <div className="text-2xl font-bold text-foreground mb-1">
            {systemDesign?.roiPercent}%
          </div>
          <div className="text-sm text-muted-foreground">
            ROI a {GARANTY_YEARS} años
          </div>
        </div>
      </div>

      {/* Informative Note */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-xl solar-shadow-sm">
        <div className="flex items-start">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5 mr-3 shrink-0" />
          <p className="text-sm text-blue-800 leading-relaxed">
            <strong>Nota Importante:</strong> Estos cálculos son estimaciones basadas en tu consumo promedio.
            Te invitamos a <strong>solicitar tu consulta gratuita</strong> o contactarnos directamente para brindarte una
            cotización técnica formal y exacta según las condiciones de tu propiedad.
          </p>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-white rounded-xl border solar-shadow-card p-6 overflow-hidden relative">
        <h3 className="text-xl font-bold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="Calculator" size={24} />
          <span>Resumen Financiero</span>
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">
              Inversión y Financiamiento
            </h4>

            <div className="space-y-3">
              {financingData?.type === "cash" &&
                financingData?.desContado > 0 && (
                  <>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-muted-foreground">
                        Costo del Sistema:
                      </span>
                      <span className="font-semibold">
                        {formatQTZ(systemDesign?.investment)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700">
                        Descuento por pago al contado:
                      </span>
                      <span className="font-semibold text-green-600">
                        {formatQTZ(
                          systemDesign?.investment *
                          (financingData?.desContado / 100)
                        )}
                      </span>
                    </div>
                  </>
                )}

              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-700">Inversión Neta:</span>
                <span className="font-semibold text-blue-600">
                  {formatQTZ(financingData?.totalCost)}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">
              Ahorros y Beneficios
            </h4>

            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-700">Ahorro Mensual:</span>
                <span className="font-semibold text-green-600">
                  {formatQTZ(systemDesign?.monthlySavings)}
                </span>
              </div>

              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="text-green-700">Ahorro Anual:</span>
                <span className="font-semibold text-green-600">
                  {formatQTZ(systemDesign?.monthlySavings * 12)}
                </span>
              </div>

              <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                <span className="text-emerald-700">
                  Ahorro Total ({GARANTY_YEARS} años):
                </span>
                <span className="font-semibold text-emerald-600">
                  {formatQTZ(financingData?.totalSavings25Years)}
                </span>
              </div>

              {financingData?.netMonthlyCost !== undefined && (
                <div
                  className={`flex justify-between items-center p-3 rounded-lg ${financingData?.netMonthlyCost < 0
                    ? "bg-green-50"
                    : "bg-orange-50"
                    }`}
                >
                  <span
                    className={
                      financingData?.netMonthlyCost < 0
                        ? "text-green-700"
                        : "text-orange-700"
                    }
                  >
                    {financingData?.netMonthlyCost < 0
                      ? "Ahorro Neto Mensual:"
                      : "Costo Neto Mensual:"}
                  </span>
                  <span
                    className={`font-semibold ${financingData?.netMonthlyCost < 0
                      ? "text-green-600"
                      : "text-orange-600"
                      }`}
                  >
                    {formatQTZ(Math.abs(financingData?.netMonthlyCost))}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Share Section (Inside Financial Summary but distinct) */}
        <div className="mt-8 pt-8 border-t border-dashed flex flex-col items-center text-center">
          <p className="text-sm text-muted-foreground mb-4 font-medium">
            ¿Conoces a alguien que pueda interesarle este ahorro?
          </p>
          <Button
            onClick={shareViaWhatsApp}
            variant="outline"
            size="sm"
            iconName="MessageCircle"
            iconPosition="left"
            className="border-green-600 text-green-700 hover:bg-green-50 px-10"
          >
            Compartir propuesta con un amigo
          </Button>
        </div>
      </div>

      {/* Monthly Production Chart */}
      <div className="bg-white rounded-xl border solar-shadow-card p-6">
        <h3 className="text-xl font-bold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="BarChart3" size={24} />
          <span>Producción Mensual Estimada</span>
        </h3>

        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-2">
            {monthlyData?.map((data, index) => (
              <div key={index} className="text-center">
                <div className="relative h-32 bg-gray-100 rounded-lg mb-2 overflow-hidden">
                  <div
                    className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary to-primary/70 rounded-lg transition-all duration-500"
                    style={{
                      height: `${(data?.production /
                        Math.max(...monthlyData?.map((d) => d?.production))) *
                        100
                        }%`,
                    }}
                  ></div>
                  <div className="absolute inset-0 flex items-end justify-center">
                    <span className="text-xs font-medium text-white">
                      {data?.production}
                    </span>
                  </div>
                </div>
                <div className="text-xs font-medium text-foreground">
                  {data?.month}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded"></div>
              <span className="text-muted-foreground">
                Producción Solar (kWh)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-300 rounded"></div>
              <span className="text-muted-foreground">
                Consumo Actual: {consumptionData?.monthlyKwh} kWh/mes
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Environmental Impact */}
      <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-green-800 flex items-center space-x-2">
            <Icon name="Leaf" size={24} />
            <span>Impacto Ambiental</span>
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowEnvironmentalImpact(!showEnvironmentalImpact)}
            iconName={showEnvironmentalImpact ? "ChevronUp" : "ChevronDown"}
            iconPosition="right"
          >
            {showEnvironmentalImpact ? "Ocultar" : "Ver Detalles"}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="text-center p-4 bg-white rounded-lg border border-green-200">
            <Icon
              name="TreePine"
              size={24}
              className="text-green-600 mx-auto mb-2"
            />
            <div className="text-lg font-bold text-green-800">
              {environmentalImpact?.treesEquivalent}
            </div>
            <div className="text-sm text-green-600">Árboles plantados por año</div>
          </div>

          <div className="text-center p-4 bg-white rounded-lg border border-green-200">
            <Icon
              name="Cloud"
              size={24}
              className="text-green-600 mx-auto mb-2"
            />
            <div className="text-lg font-bold text-green-800">
              {formatNumKgT(environmentalImpact?.annualCO2Reduction)}
            </div>
            <div className="text-sm text-green-600">CO₂ evitado por año</div>
          </div>

          <div className="text-center p-4 bg-white rounded-lg border border-green-200">
            <Icon
              name="Zap"
              size={24}
              className="text-green-600 mx-auto mb-2"
            />
            <div className="text-lg font-bold text-green-800">
              {formatNumKgT(environmentalImpact?.coalAvoided)}
            </div>
            <div className="text-sm text-green-600">
              Carbón evitado por año
            </div>
          </div>
        </div>

        {showEnvironmentalImpact && (
          <div className="bg-white rounded-lg p-4 border border-green-200">
            <h4 className="font-semibold text-green-800 mb-3">
              Tu Contribución al Medio Ambiente
            </h4>
            <div className="space-y-2 text-sm text-green-700">
              <p>
                • En {GARANTY_YEARS} años, tu sistema solar evitará la emisión
                de{" "}
                <strong>
                  {formatNumKgT(
                    environmentalImpact?.annualCO2Reduction * GARANTY_YEARS
                  )}{" "}
                  de CO₂
                </strong>
              </p>
              <p>
                • Equivale a plantar{" "}
                <strong>
                  {formatNumMil(
                    environmentalImpact?.treesEquivalent * GARANTY_YEARS
                  )}{" "}
                  árboles
                </strong>{" "}
                y mantenerlos durante {GARANTY_YEARS} años
              </p>
              <p>
                • Contribuyes directamente a los objetivos de Guatemala en
                reducir{" "}
                <strong>
                  {formatNumKgT(environmentalImpact?.coalAvoided * GARANTY_YEARS)}{" "}
                  emisiones de carbón{" "}
                </strong>{" "}
                durante {GARANTY_YEARS} años
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Contact Form */}
      <div className="bg-white rounded-xl border solar-shadow-card p-6">
        <h3 className="text-xl font-bold text-foreground mb-6 flex items-center space-x-2">
          <Icon name="Calendar" size={24} />
          <span>Solicitar Consulta Gratuita</span>
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Nombre Completo *
              </label>
              <input
                type="text"
                value={contactInfo?.name}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, name: e?.target?.value })
                }
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: Juan Carlos Pérez"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Correo Electrónico *
              </label>
              <input
                type="email"
                value={contactInfo?.email}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, email: e?.target?.value })
                }
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: juan.perez@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Teléfono *
              </label>
              <input
                type="tel"
                value={contactInfo?.phone}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, phone: e?.target?.value })
                }
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ej: +502 1234-5678"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Método de Contacto Preferido
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="phone"
                    checked={contactInfo?.preferredContact === "phone"}
                    onChange={(e) =>
                      setContactInfo({
                        ...contactInfo,
                        preferredContact: e?.target?.value,
                      })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Teléfono</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="email"
                    checked={contactInfo?.preferredContact === "email"}
                    onChange={(e) =>
                      setContactInfo({
                        ...contactInfo,
                        preferredContact: e?.target?.value,
                      })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="whatsapp"
                    checked={contactInfo?.preferredContact === "whatsapp"}
                    onChange={(e) =>
                      setContactInfo({
                        ...contactInfo,
                        preferredContact: e?.target?.value,
                      })
                    }
                    className="mr-2"
                  />
                  <span className="text-sm">WhatsApp</span>
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-primary/5 rounded-lg p-4">
              <h4 className="font-semibold text-foreground mb-3">
                ¿Qué incluye la consulta gratuita?
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-green-600" />
                  <span>Evaluación técnica del techo</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-green-600" />
                  <span>Diseño personalizado del sistema</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-green-600" />
                  <span>Análisis de sombreado</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-green-600" />
                  <span>Propuesta económica detallada</span>
                </li>
                <li className="flex items-center space-x-2">
                  <Icon name="Check" size={14} className="text-green-600" />
                  <span>Opciones de financiamiento</span>
                </li>
              </ul>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon
                  name="Clock"
                  size={16}
                  className="text-yellow-600 mt-0.5"
                />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">
                    Respuesta
                  </p>
                  <p className="text-yellow-700">
                    Nos contactaremos contigo lo antes posible
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col gap-4">
        <p className="text-center font-bold text-foreground uppercase tracking-wider text-sm">
          Solicitar Consulta Gratuita
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            onClick={handleRequestConsultation}
            size="xl"
            variant="primary"
            iconName="Mail"
            iconPosition="left"
            className="w-full sm:w-auto px-10 shadow-lg shadow-primary/20"
            disabled={
              !contactInfo?.name || !contactInfo?.email || !contactInfo?.phone
            }
          >
            Enviar Correo
          </Button>

          <Button
            onClick={contactViaWhatsApp}
            size="xl"
            variant="success"
            iconName="MessageCircle"
            iconPosition="left"
            className="w-full sm:w-auto px-10 shadow-lg shadow-green-600/20 bg-green-600 hover:bg-green-700"
            disabled={
              !contactInfo?.name || !contactInfo?.email || !contactInfo?.phone
            }
          >
            Enviar WhatsApp
          </Button>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="bg-muted/30 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Recursos Adicionales
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/proyectos"
            className="flex items-center space-x-3 p-4 bg-white rounded-lg border hover:border-primary/50 transition-colors"
          >
            <Icon name="Building2" size={20} className="text-primary" />
            <div>
              <p className="font-medium text-foreground">Ver Proyectos</p>
              <p className="text-sm text-muted-foreground">
                Casos de éxito similares
              </p>
            </div>
          </Link>

          <Link
            to="/servicios"
            className="flex items-center space-x-3 p-4 bg-white rounded-lg border hover:border-primary/50 transition-colors"
          >
            <Icon name="Settings" size={20} className="text-primary" />
            <div>
              <p className="font-medium text-foreground">Nuestros Servicios</p>
              <p className="text-sm text-muted-foreground">Proceso completo</p>
            </div>
          </Link>

          <Link
            to="/nosotros"
            className="flex items-center space-x-3 p-4 bg-white rounded-lg border hover:border-primary/50 transition-colors"
          >
            <Icon name="Info" size={20} className="text-primary" />
            <div>
              <p className="font-medium text-foreground">Sobre Nosotros</p>
              <p className="text-sm text-muted-foreground">
                Conoce nuestro equipo
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResultsStep;
